import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import nightRestService from "../services/nightRestService";

function formatUntil(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DeviceNightRestStatus({ udid }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(Boolean(udid));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canEdit = isAdministrator();

  const load = useCallback(async () => {
    if (!udid) {
      setState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setState(await nightRestService.getDeviceState(udid));
      setError("");
    } catch (err) {
      setState(null);
      setError(err.response?.data?.message || "Status nicht verfügbar");
    } finally {
      setLoading(false);
    }
  }, [udid]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (mode) => {
    if (!udid || saving) return;
    setSaving(true);
    try {
      const next = await nightRestService.setDeviceOverride(udid, { mode });
      setState(next);
      if (mode === "off") toast.success("Nachtruhe für dieses Gerät vorübergehend aufgehoben.");
      else if (mode === "on") toast.success("Nachtruhe für dieses Gerät vorübergehend eingeschaltet.");
      else toast.success("Gerät folgt wieder dem Zeitplan.");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Override fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  if (!udid) return "—";
  if (loading && !state) return <span className="text-muted">lädt…</span>;
  if (!state) return <span className="text-muted">{error || "nicht verfügbar"}</span>;

  const names = (state.policies || []).map((row) => row.name).filter(Boolean);
  const untilLabel = formatUntil(state.override?.until || state.untilDefault);
  const overrideHint = state.override?.mode === "off"
    ? `temporär aufgehoben bis ${formatUntil(state.override.until)}`
    : state.override?.mode === "on"
      ? `temporär eingeschränkt bis ${formatUntil(state.override.until)}`
      : (state.applicable ? "folgt dem Zeitplan" : null);

  let badge;
  if (!state.applicable && !state.applied) {
    badge = { className: "text-bg-secondary", label: "nicht zugewiesen" };
  } else if (state.restricted) {
    badge = { className: "text-bg-danger", label: "eingeschränkt" };
  } else {
    badge = { className: "text-bg-success", label: "nicht eingeschränkt" };
  }

  return (
    <div>
      <span className={`badge ${badge.className}`}>{badge.label}</span>
      {overrideHint ? <div className="small text-muted mt-1">{overrideHint}</div> : null}
      {state.restricted !== state.applied ? (
        <div className="small text-muted mt-1">Änderung wird an das Gerät gesendet…</div>
      ) : null}
      {names.length ? (
        <div className="small text-muted mt-1">{names.join(", ")}</div>
      ) : null}
      {canEdit && state.canOverride ? (
        <div className="d-flex flex-wrap gap-1 mt-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            disabled={saving}
            title={untilLabel ? `Gilt bis ${untilLabel}` : undefined}
            onClick={() => apply("off")}
          >
            Aufheben
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={saving}
            title={untilLabel ? `Gilt bis ${untilLabel}` : undefined}
            onClick={() => apply("on")}
          >
            Einschränken
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={saving || !state.override}
            onClick={() => apply("schedule")}
          >
            Zeitplan
          </button>
        </div>
      ) : null}
    </div>
  );
}
